// PT_SURVEY_BENEFIT_MODAL：供 Ptengine 调研完成后调用，对外方法为 window.openSurveyBenefitModal()。
class SurveyBenefitModalElement extends HTMLElement {
  constructor() {
    super();
    this.closeTimer = null;
    this.copyTimer = null;
    this.returnFocus = null;
    this.handleCancel = this.handleCancel.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.dialog = this.querySelector('dialog');
    this.panel = this.querySelector('.survey-benefit-modal__panel');
    this.closeButton = this.querySelector('[data-survey-modal-close]');
    this.copyButton = this.querySelector('[data-survey-copy-code]');
    this.codeElement = this.querySelector('[data-survey-discount-code]');

    if (!this.dialog || !this.panel || !this.closeButton || !this.copyButton || !this.codeElement) return;

    this.defaultCode = this.codeElement.dataset.defaultCode;
    this.defaultCopyLabel = this.copyButton.dataset.defaultLabel;
    this.currentCopyLabel = this.defaultCopyLabel;

    this.closeButton.addEventListener('click', () => this.close());
    this.copyButton.addEventListener('click', () => this.copyDiscountCode());
    this.dialog.addEventListener('cancel', this.handleCancel);
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('close', () => this.afterClose());
    this.initialized = true;
  }

  open(options = {}) {
    if (!this.initialized) return false;

    const config = typeof options === 'string' ? { discountCode: options } : options || {};
    const discountCode = String(config.discountCode || this.defaultCode).trim();
    this.currentCopyLabel = String(config.copyButtonText || this.defaultCopyLabel).trim();
    this.codeElement.textContent = discountCode;
    this.copyButton.textContent = this.currentCopyLabel;
    this.returnFocus = document.activeElement;

    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.copyTimer);
    this.dialog.classList.remove('is-closing');

    if (!this.dialog.open) this.dialog.showModal();

    document.body.classList.add('survey-benefit-modal-open');
    window.requestAnimationFrame(() => {
      this.dialog.classList.add('is-visible');
      this.dialog.focus({ preventScroll: true });
    });

    return true;
  }

  close() {
    if (!this.dialog?.open || this.dialog.classList.contains('is-closing')) return false;

    this.dialog.classList.remove('is-visible');
    this.dialog.classList.add('is-closing');
    const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;

    this.closeTimer = window.setTimeout(() => {
      if (this.dialog.open) this.dialog.close();
    }, closeDelay);

    return true;
  }

  handleCancel(event) {
    event.preventDefault();
    this.close();
  }

  handleDialogClick(event) {
    if (event.target !== this.dialog) return;

    const panelBounds = this.panel.getBoundingClientRect();
    const clickedOutsidePanel =
      event.clientX < panelBounds.left ||
      event.clientX > panelBounds.right ||
      event.clientY < panelBounds.top ||
      event.clientY > panelBounds.bottom;

    if (clickedOutsidePanel) this.close();
  }

  async copyDiscountCode() {
    const discountCode = this.codeElement.textContent.trim();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(discountCode);
      } else {
        this.copyWithFallback(discountCode);
      }
      this.showCopyStatus(this.copyButton.dataset.successLabel);
    } catch (error) {
      try {
        this.copyWithFallback(discountCode);
        this.showCopyStatus(this.copyButton.dataset.successLabel);
      } catch (fallbackError) {
        console.error('Unable to copy the survey discount code.', fallbackError);
        this.showCopyStatus(this.copyButton.dataset.errorLabel);
      }
    }
  }

  copyWithFallback(value) {
    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand('copy');
    textArea.remove();

    if (!copied) throw new Error('Copy command was rejected.');
  }

  showCopyStatus(label) {
    window.clearTimeout(this.copyTimer);
    this.copyButton.textContent = label;
    this.copyTimer = window.setTimeout(() => {
      this.copyButton.textContent = this.currentCopyLabel;
    }, 1800);
  }

  afterClose() {
    this.dialog.classList.remove('is-visible', 'is-closing');
    document.body.classList.remove('survey-benefit-modal-open');

    if (this.returnFocus instanceof HTMLElement && document.contains(this.returnFocus)) {
      this.returnFocus.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('survey-benefit-modal')) {
  customElements.define('survey-benefit-modal', SurveyBenefitModalElement);
}

window.openSurveyBenefitModal = (options) => {
  return document.querySelector('survey-benefit-modal')?.open(options) || false;
};

window.closeSurveyBenefitModal = () => {
  return document.querySelector('survey-benefit-modal')?.close() || false;
};
